import {Injectable, Injector} from '@angular/core';
import {AuthenticateWebSocketHandlerService} from './websocket/authenticate-web-socket-handler.service';
import {WebSocketService} from './websocket/web-socket.service';
import {OrderPolicyService} from './order-policy.service';
import {PaymentWebSocketHandlerService} from './websocket/payment-web-socket-handler.service';
import {isEmbeddedInIframe, isNestBank, isSkycash, redirectParentTo, redirectTo} from '../../utils/utils';
import {SessionService} from './session/session.service';
import {RegistrationService} from './registration.service';
import {NotificationPath, NotificationService} from './notification.service';
import {ApplicationStateService} from '../../../shared/services/application-state.service';
import {EventBusService} from './event-bus.service';
import {NotificationMode, NotificationStateService} from '../../../shared/services/notification-state.service';
import {SessionDetailsResponse} from '../../models/session-details-response.model';
import {PaymentStatus} from '../../../shared/components/notification-wrapper/notification/notification.component';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {BusinessParamName} from '../../../shared/models/request-offer/request-offer.enum';
import {
  PaymentAuthorizationStatus
} from '../../../shared/sections/notifications/insurer-data-authorization-notification/insurer-data-authorization.component';
import {AppMode} from '../../../shared/models/app-mode.enum';
import {SalesChannel} from '../../consts/sales-channel.enum';
import {OfferModificationsService} from './offer-modifications/offer-modifications.service';
import {MatDialog} from '@angular/material';
import {SelectedOfferService} from './section-services/selected-offer/selected-offer.service';
import {ApplicationEvent, ApplicationEventsService} from '../../../shared/services/application-events.service';
import {CalculationService} from './calculation.service';
import {UrlParamsService} from './url-params.service';
import {MetricsService} from '../../../shared/services/metrics.service';
import {BusinessParameter} from '../../../shared/models/request-offer/request-header.model';
import {SalesChannelAnalyticsService} from '../../../shared/services/sales-channel-analytics.service';
import {QueryParamName} from '../../enums/query-param-name.enum';
import {
  IframePaymentRedirectBehaviour,
  NativeIframeCommunicationService
} from '../../../shared/services/native-iframe-communication.service';
import {SessionStatusCode} from '../../enums/session-status-code.enum';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {

  private subscriptions = [];
  private paymentStatus: number;
  private webSocketService: WebSocketService;
  private paymentStatusFromSessionTimeout;
  private supportedPaymentStatuses = [200, 402, 406, 407, 409];
  private orderPolicySent: boolean = false;

  get hasModifications(): boolean {
    return this.offerModificationsService.offerModificationsEnabled && this.selectedOfferService.hasModifications;
  }

  constructor(private injector: Injector,
              private registrationService: RegistrationService,
              private calculationService: CalculationService,
              private sessionService: SessionService,
              private orderPolicyService: OrderPolicyService,
              private metricsService: MetricsService,
              private eventBusService: EventBusService,
              private dialog: MatDialog,
              private urlParamsService: UrlParamsService,
              private eventsService: ApplicationEventsService,
              private selectedOfferService: SelectedOfferService,
              private applicationState: ApplicationStateService,
              private notificationService: NotificationService,
              private offerModificationsService: OfferModificationsService,
              private salesChannelAnalyticsService: SalesChannelAnalyticsService,
              private notificationStateService: NotificationStateService,
              private nativeIframeCommunicationService: NativeIframeCommunicationService) {
  }

  disconnectWS(): void {
    if (isNotNullOrUndefined(this.webSocketService)) {
      this.webSocketService.disconnect();
    }
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.cancelPaymentStatusFromSessionUpdate();
  }

  initPayment() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.initWebSocketPaymentService();
  }

  initPaymentStatus(): void {
    if (this.notificationStateService.canInitPaymentStatusFromSession) {
      this.getPaymentStatusFromSession();
    }
    this.initPaymentStatusFromWebSocket();
  }

  private getPaymentStatusFromSession(): any {
    this.sessionService.getSessionDetails(false)
      .then(resp => {
        if (resp.values) {
          const calculationSimpleId = this.sessionService.getBusinessParameter(resp, BusinessParamName.CALCULATION_SIMPLE_ID);
          this.applicationState.saveBusinessParameter(BusinessParamName.CALCULATION_SIMPLE_ID, calculationSimpleId);
        }
        this.handlePaymentStatusFromSession(resp);
      })
      .catch(error => {
        if (error && error.status === 404) {
          this.notificationStateService.handleNoPolicyInfo();
        }
      })
      .finally(() => this.notificationStateService.setAppMode(AppMode.READONLY));
  }

  private initPaymentStatusFromWebSocket(): void {
    this.notificationService.notificationSubject.asObservable().subscribe(notification => {
      if (isNotNullOrUndefined(notification)) {
        if (notification.body.hasOwnProperty('uri')) {
          if (notification.body.statusCode === SessionStatusCode.WAITING_FOR_NEW_AMOUNT_ACCEPT) {
            redirectTo(notification.body.uri);
          } else {
            if (this.shouldRemoveApplicationState(notification.body.uri)) {
              this.applicationState.removeApplicationState();
            }
            this.applicationState.redirectedToPayment = true;
            this.nativeIframeCommunicationService.sendOfferPayEvent(notification.body.uri);
            this.redirectToPayment(notification.body.uri);
          }
        } else if (notification.body.hasOwnProperty('policyAmount')) {
          if (notification.hasOwnProperty('header')) {
            this.processNotificationHeader(notification.header);
          }
          this.eventsService.sendEvent(ApplicationEvent.OFFER_PAY, this.buildPayloadForEvent(notification.body));
        } else {
          if (notification.hasOwnProperty('header')) {
            this.processNotificationHeader(notification.header);
          }
          this.notificationStateService.setAppMode(AppMode.READONLY);
          if (notification.body.status.status === 200) {
            this.salesChannelAnalyticsService.sendDataAfterPaymentConfirmation();
            this.notificationService.redirectToNotification(NotificationPath.ACCEPT, {
              [QueryParamName.SESSION_ID]: this.sessionService.sessionId,
            })
          }
          if (notification.body.status.status === 409) {
            this.notificationService.redirectToNotification(NotificationPath.ACCEPT, {
              [QueryParamName.SESSION_ID]: this.sessionService.sessionId,
            })
          }
          if (notification.body.status === 406) {
            this.notificationService.redirectToNotification(NotificationPath.AUTHORIZATION, {
              [QueryParamName.SESSION_ID]: this.sessionService.sessionId,
            })
          }
          if (notification.body.status === 407) {
            this.notificationService.redirectToNotification(NotificationPath.CANCEL, {
              [QueryParamName.SESSION_ID]: this.sessionService.sessionId,
            })
          }
          setTimeout(() => this.notificationStateService.buildWebSocketPaymentNotification(notification), 0);
        }
      }
    });
  }

  private buildPayloadForEvent(body: any): any {
    let payload: any;

    if (isNestBank()) {
      payload = {
        offerId: body.offerId,
      };
    } else {
      const {registrationNumber} = this.calculationService.calculationData.vehicle;
      payload = {
        registrationNumber: (registrationNumber && registrationNumber.length > 0) ? registrationNumber :
            this.applicationState.state.registrationNumberFromSession,
        insurerName: this.applicationState.getBusinessParameterValue(BusinessParamName.INSURANCE_COMPANY_CODE),
        offerId: body.offerId,
        orderId: this.applicationState.getBusinessParameterValue(BusinessParamName.ORDER_ID),
        amount: body.policyAmount + ''
      };

      if (isSkycash()) {
        payload.calculationId = this.applicationState.getBusinessParameterValue(BusinessParamName.CALCULATION_ID)
      }
    }

    return payload;
  }

  private processNotificationHeader(header: any): void {
    const applicationIdParam: BusinessParameter = header.processContext.businessParameters.find((param: BusinessParameter) => param.parameterName === BusinessParamName.APPLICATION_ID);
    const applicationId = applicationIdParam ? applicationIdParam.parameterValue : undefined;
    if (this.applicationState.salesChannel === SalesChannel.BANKIER && isNotNullOrUndefined(applicationId)) {
      this.salesChannelAnalyticsService.bankierId = <string>applicationId;
    }

    const partnerExternalIdParam: BusinessParameter = header.processContext.businessParameters.find((param: BusinessParameter) => param.parameterName === BusinessParamName.PARTNER_EXTERNAL_ID);
    const partnerExternalId = partnerExternalIdParam ? partnerExternalIdParam.parameterValue : undefined;
    if (isNotNullOrUndefined(partnerExternalId)) {
      this.urlParamsService.setQueryParam(QueryParamName.PARTNER_EXTERNAL_ID, <string>partnerExternalId);
    }
  }

  private initWebSocketPaymentService() {
    let authenticateWebSocketHandlerService = this.injector.get(AuthenticateWebSocketHandlerService);
    this.webSocketService = this.injector.get(WebSocketService);
    let paymentWebSocketHandlerService: PaymentWebSocketHandlerService = this.injector.get(PaymentWebSocketHandlerService);

    let paymentSubscription = paymentWebSocketHandlerService.paymentObservable
      .subscribe(payment => {
        if (!this.registrationService.paymentWithDeepLink) {
          this.webSocketService.disconnect();
        }
        if (isNotNullOrUndefined(payment)) {
          this.notificationService.sendNotificationMetric(payment, true);
        }
        this.handlePayment(payment);
      });
    this.subscriptions.push(paymentSubscription);

    let authenticationSubscription = authenticateWebSocketHandlerService.authObservable.subscribe(() => {
      if (!this.orderPolicySent) {
        this.orderPolicySent = true;
        this.orderPolicyService.submitOrderPolicy()
            .then(value => {
            })
            .catch(err => {
            });
      }
    });
    this.subscriptions.push(authenticationSubscription);
    if (!this.webSocketService.isConnected || this.hasModifications) {
      this.webSocketService.connect();
    }
  }

  private handlePayment(payment: any) {
    if (payment.hasOwnProperty('uri')) {
      if (payment.statusCode === SessionStatusCode.WAITING_FOR_NEW_AMOUNT_ACCEPT) {
        redirectTo(payment.uri);
      } else {
        if (this.shouldRemoveApplicationState(payment.uri)) {
          this.applicationState.removeApplicationState();
        }
        this.applicationState.redirectedToPayment = true;
        this.nativeIframeCommunicationService.sendOfferPayEvent(payment.uri);
        this.redirectToPayment(payment.uri);
      }
    } else if (payment.hasOwnProperty('policyAmount')) {
      this.eventsService.sendEvent(ApplicationEvent.OFFER_PAY, this.buildPayloadForEvent(payment));
    } else {
      if (payment.hasOwnProperty('status') && this.canHandleStatus(payment.status)) {
        this.handlePaymentWithStatus(payment);
      }
    }
  }

  private handlePaymentWithStatus(payment: any): void {
    this.eventBusService.clearActiveStepEvent.emit();
    this.notificationStateService.setNotificationMode(NotificationMode.PAYMENT_STATUS);
    const path = this.notificationService.getNotificationPath(payment.status);
    this.notificationService.redirectToNotification(path, {
      [QueryParamName.SESSION_ID]: this.sessionService.sessionId,
    });
    this.notificationService.emitNotification({
      body: {
        status: payment
      }
    });
  }

  private handlePaymentStatusFromSession(resp: SessionDetailsResponse) {
    if (resp.values && resp.values.status) {
      this.processPaymentStatus(resp.values.status);
    } else {
      this.notificationStateService.handleNoPolicyInfo();
    }
  }

  private processPaymentStatus(paymentStatus: PaymentStatus | PaymentAcceptStatus | PaymentAuthorizationStatus) {
    this.notificationService.sendNotificationMetric(paymentStatus, false);
    if (paymentStatus.hasOwnProperty('status')) {
      this.paymentStatus = (<PaymentAcceptStatus>paymentStatus).status;
      this.notificationStateService.setPaymentAcceptNotification(<PaymentAcceptStatus>paymentStatus);
    } else if (paymentStatus.hasOwnProperty('code')) {
      const status = <PaymentStatus>paymentStatus;
      this.paymentStatus = status.code;
      switch (status.code) {
        case 202: // waiting
          this.handleWaitingPaymentStatus(status);
          break;
        case 200: // success
          this.salesChannelAnalyticsService.sendDataAfterPaymentConfirmation();
          this.notificationStateService.buildNotificationForResolvedPaymentStatus(status);
          break;
        case 402: // error
          this.notificationStateService.buildNotificationForResolvedPaymentStatus(status);
          break;
        case 406: // link4 authorization data
          this.notificationStateService.setAuthorizationInsurerDataNotification(<PaymentAuthorizationStatus>paymentStatus);
          break;
        case 407: // failed registration data
          this.notificationStateService.setRegistrationFailedNotification(status);
          break;
        case 409: // waiting for new amount accept
          this.notificationStateService.setPaymentAcceptNotification(<PaymentAcceptStatus>paymentStatus);
          break;
        default:
      }
    }
  }

  private handleWaitingPaymentStatus(paymentStatus) {
    this.notificationStateService.setMessage(paymentStatus.message);
    this.paymentStatusFromSessionTimeout = setTimeout(() => {
      this.getPaymentStatusFromSession();
    }, 10000);
  }

  private handleSubmitOrderPolicyResponse(success: boolean): void {
    const queryParams = {
      [QueryParamName.SESSION_ID]: this.sessionService.sessionId,
    };
    this.notificationStateService.setNotificationMode(NotificationMode.PAYMENT_STATUS);
    const notificationPath = success ? NotificationPath.SUCCESS : NotificationPath.CANCEL;
    this.notificationService.redirectToNotification(notificationPath, queryParams);
  }

  private canHandleStatus(status: number): boolean {
    return this.supportedPaymentStatuses.includes(status);
  }

  private shouldRemoveApplicationState(paymentUri: string): boolean {
    return !paymentUri.includes('notification') && !this.registrationService.paymentWithDeepLink;
  }

  private cancelPaymentStatusFromSessionUpdate(): void {
    if (isNotNullOrUndefined(this.paymentStatusFromSessionTimeout)) {
      clearTimeout(this.paymentStatusFromSessionTimeout);
    }
  }

  private redirectToPayment(uri: string): void {
    if (isEmbeddedInIframe()) {
      switch (this.nativeIframeCommunicationService.paymentRedirectBehaviour) {
        case IframePaymentRedirectBehaviour.IFRAME_REDIRECT:
          redirectTo(uri);
          break;
        case IframePaymentRedirectBehaviour.PARENT_REDIRECT:
          redirectParentTo(uri);
          break;
      }
    } else {
      redirectTo(uri);
    }
  }
}

export interface PaymentAcceptStatus {
  amount: string;
  newAmount: string;
  instalmentAmount: number;
  newInstalmentAmount: number;
  instalmentCode?: string;
  status?: number;
  code?: number;
  statusCode?: string;
  message?: string;
}
